import { Trans } from 'next-i18next'
import { Box, Button, Inline, Stack, TextLink } from '@machobear/design-system'
import { Form, Formik, FormikHelpers, FormikProps } from 'formik'

import { requestResetPassword } from '@/models/auth/ports'
import Yup from '@/lib/yup'
import { FieldInput } from '@/components/form'
import { toast } from '@/components/toast-notifications'

import { useLogin } from './context'
import { LoginStepProps } from './types'

type ForgotPasswordProps = Pick<
  LoginStepProps,
  'afterReset' | 'setCurrentStep' | 'title' | 'description' | 't'
>

type Form = {
  username: string
}

const ForgotPassword: React.FC<ForgotPasswordProps> = ({
  afterReset,
  setCurrentStep,
  t
}) => {
  const { username } = useLogin()

  const initialValues = { username: username || '' }
  const validationSchema = Yup.object().shape({
    username: Yup.string().email().required()
  })
  const onSubmit = async (
    values: Form,
    { setSubmitting }: FormikHelpers<Form>
  ) => {
    const { response } = await requestResetPassword(values)
    setSubmitting(false)
    if (!response.ok) {
      toast(
        <Trans t={t} i18nKey="b.auth.edit.reset_failed.text">
          Oops! Something went wrong. Please try again or if the issue persists,{' '}
          <TextLink
            target="_blank"
            rel="noopener noreferrer"
            href="https://go.crisp.chat/chat/embed/?website_id=c0b25025-dce5-4e32-ba4f-fdd427e205ef"
          >
            please contact our support team.
          </TextLink>
        </Trans>,
        {
          title: t('b.auth.edit.reset_failed.title', 'Password Reset Failed'),
          type: 'error'
        }
      )
      return
    }
    window.dataLayer.push({
      event: 'web-login-reset-password-submit-button'
    })
    setCurrentStep('resetPasswordSuccess')
    if (afterReset) afterReset()
  }

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, isValid }: FormikProps<Form>) => (
          <Box component={Form} noValidate width="full">
            <Stack space="medium">
              <FieldInput
                name="username"
                autoComplete="section-reset-request username email"
                autoFocus
                placeholder={t('common.email', 'Email')}
              />
              <Stack space="small">
                <Button
                  id="web-reset-password-button"
                  disabled={isSubmitting || !isValid}
                  type="submit"
                >
                  {t('b.auth.view.reset_password.button', 'Reset Password')}
                </Button>
                <Inline space="none" align="center">
                  <Button
                    variant="transparent"
                    type="button"
                    onClick={() => setCurrentStep('login')}
                  >
                    {t('common.back', 'Back')}
                  </Button>
                </Inline>
              </Stack>
            </Stack>
          </Box>
        )}
      </Formik>
    </>
  )
}

export default ForgotPassword
